import { useState } from 'react';
import { documentService } from '@services/document.service';
import { logger } from '@utils/logger';

export const useDocument = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);

  const uploadDocument = async (documentType, file) => {
    setLoading(true);
    setError(null);
    setUploadProgress(0);
    try {
      const response = await documentService.uploadDocument(
        documentType,
        file
      );
      setUploadProgress(100);
      setLoading(false);
      return response;
    } catch (err) {
      setError(err.message || 'Failed to upload document');
      setLoading(false);
      throw err;
    }
  };

  const getMyDocuments = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await documentService.getMyDocuments();
      setLoading(false);
      return response;
    } catch (err) {
      setError(err.message || 'Failed to get documents');
      setLoading(false);
      throw err;
    }
  };

  const getPendingDocuments = async (page = 1, limit = 20) => {
    setLoading(true);
    setError(null);
    try {
      const response = await documentService.getPendingDocuments(page, limit);
      setLoading(false);
      return response;
    } catch (err) {
      setError(err.message || 'Failed to get pending documents');
      setLoading(false);
      throw err;
    }
  };

  const approveDocument = async (documentId, notes = null) => {
    setLoading(true);
    setError(null);
    try {
      const response = await documentService.approveDocument(documentId, notes);
      setLoading(false);
      return response;
    } catch (err) {
      setError(err.message || 'Failed to approve document');
      setLoading(false);
      throw err;
    }
  };

  const rejectDocument = async (documentId, reason, notes = null) => {
    setLoading(true);
    setError(null);
    try {
      const response = await documentService.rejectDocument(documentId, reason, notes);
      setLoading(false);
      return response;
    } catch (err) {
      setError(err.message || 'Failed to reject document');
      setLoading(false);
      throw err;
    }
  };

  const downloadDocument = async (documentId, fileName) => {
    setLoading(true);
    setError(null);
    try {
      const blob = await documentService.downloadDocument(documentId);
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName || 'document';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      setLoading(false);
    } catch (err) {
      setError(err.message || 'Failed to download document');
      setLoading(false);
      throw err;
    }
  };

  const clearError = () => setError(null);

  return {
    loading,
    error,
    uploadProgress,
    uploadDocument,
    getMyDocuments,
    getPendingDocuments,
    approveDocument,
    rejectDocument,
    downloadDocument,
    clearError,
  };
};

export default useDocument;
